"use client";
import {
  FacebookIcon,
  TikTokIcon,
  TwitterIcon,
  LinkedinIcon,
  YoutubeIcon,
  InstagramIcon,
} from "../components/Icon";
import { CompanyInfo } from "src/types";
import { useFetchCompanyInfo } from "./fetchCompanyInfo";

// Define the structure of a Social Media Link
export interface SocialMediaLink {
  name: keyof CompanyInfo; // Ensures only valid keys are used
  icon: React.ReactElement;
  url: string;
}

// Default social media icons
const defaultSocialMediaIcons: Record<
  keyof Pick<
    CompanyInfo,
    "facebook" | "instagram" | "linkedin" | "x" | "youtube" | "tiktok"
  >,
  React.ReactElement
> = {
  facebook: <FacebookIcon />,
  instagram: <InstagramIcon />,
  linkedin: <LinkedinIcon />,
  x: <TwitterIcon />, // Twitter is now "X"
  youtube: <YoutubeIcon />,
  tiktok: <TikTokIcon />,
};

/**
 * Function to extract valid social media links.
 * @param companyInfo - The fetched company information.
 * @param customIcons - (Optional) Custom icons to override default ones.
 * @returns A list of valid social media links.
 */
const getValidSocialMediaLinks = (
  companyInfo: CompanyInfo | null,
  customIcons?: Partial<Record<keyof CompanyInfo, React.ReactElement>>
): SocialMediaLink[] => {
  if (!companyInfo) return [];
  return Object.entries(defaultSocialMediaIcons)
    .filter(([key]) => companyInfo[key as keyof CompanyInfo]) // Ensure the key has a valid URL
    .map(([key, defaultIcon]) => ({
      name: key as keyof CompanyInfo,
      icon: customIcons?.[key as keyof CompanyInfo] || defaultIcon, // Use custom icon if provided
      url: companyInfo[key as keyof CompanyInfo] as string, // Get actual URL
    }));
};

/**
 * Hook that fetches company info and extracts valid social media links.
 * @param customIcons - (Optional) Custom icons for social media links.
 * @returns Loading state and valid social media links.
 */
export const useFetchValidSocialMediaLinks = (
  customIcons?: Partial<Record<keyof CompanyInfo, React.ReactElement>>
): {
  loading: boolean;
  socialMediaLinks: SocialMediaLink[];
} => {
  const { data, isLoading, error } = useFetchCompanyInfo();
  if (!isLoading && !data?.websiteSettings?.social_link_visibility)
    return { loading: false, socialMediaLinks: [] };
  if (isLoading) return { loading: true, socialMediaLinks: [] };
  if (error) return { loading: false, socialMediaLinks: [] };

  return {
    loading: false,
    socialMediaLinks: getValidSocialMediaLinks(
      data?.companyInfo || null,
      customIcons
    ),
  };
};
